import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { authAPI } from '@/utils/api';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, ShieldCheck } from 'lucide-react';

const Auth = ({ onLogin }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get('tab') === 'register' ? 'register' : 'login';
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ email: '', password: '', name: '', company: '' });
  const [acceptedCGU, setAcceptedCGU] = useState(false);
  const [loading, setLoading] = useState(false);
  // 2FA state
  const [twoFA, setTwoFA] = useState(null); // { challenge_id, email_hint }
  const [code, setCode] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await authAPI.login(loginData);
      if (response.data?.requires_2fa) {
        setTwoFA({
          challenge_id: response.data.challenge_id,
          email_hint: response.data.email_hint,
        });
        toast.success('Code de vérification envoyé par email');
      } else {
        onLogin(response.data.user);
        toast.success('Connexion réussie');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    if (!code || code.length < 4) {
      toast.error('Saisissez le code reçu par email');
      return;
    }
    setLoading(true);
    try {
      const response = await authAPI.verify2fa(twoFA.challenge_id, code);
      onLogin(response.data.user);
      toast.success('Connexion administrateur réussie');
    } catch (error) {
      console.error('2FA verify error:', error);
      toast.error(error.response?.data?.detail || 'Code incorrect');
    } finally {
      setLoading(false);
    }
  };

  const handleResend2FA = async () => {
    if (!twoFA?.challenge_id) return;
    setLoading(true);
    try {
      await authAPI.resend2fa(twoFA.challenge_id);
      toast.success('Nouveau code envoyé par email');
      setCode('');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Impossible d'envoyer un nouveau code");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel2FA = () => {
    setTwoFA(null);
    setCode('');
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!acceptedCGU) {
      toast.error('Vous devez accepter les CGU/CGV et la Politique de confidentialité pour créer un compte.');
      return;
    }
    setLoading(true);
    try {
      const response = await authAPI.register({ ...registerData, accepted_cgu: true });
      onLogin(response.data);
      toast.success('Compte créé avec succès');
    } catch (error) {
      console.error('Register error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'inscription');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div
        className="hidden lg:block lg:w-1/2 bg-cover bg-center relative"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1610300068835-8a5733ae0ed1?crop=entropy&cs=srgb&fm=jpg&q=85)',
        }}
      >
        <div className="absolute inset-0 bg-primary/40 backdrop-blur-[2px]"></div>
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="text-white text-center max-w-md">
            <img src="/logo.png" alt="DIP PILOTE +IA" className="mx-auto mb-6 h-24 w-24 object-contain rounded-xl bg-white/90 p-2 shadow-lg" />
            <h1 className="text-4xl font-semibold mb-4 tracking-tight">DIP PILOTE +IA</h1>
            <p className="text-lg leading-relaxed opacity-90">
              Générez automatiquement vos Dossiers d'Information Produit conformes aux normes européennes CE 1223/2009
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Auth form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors">
            <ArrowLeft size={16} /> Retour à l'accueil
          </button>
          <div className="mb-8 text-center lg:hidden">
            <img src="/logo.png" alt="DIP PILOTE +IA" className="mx-auto mb-4 h-16 w-16 object-contain" />
            <h1 className="text-3xl font-semibold text-primary mb-2">DIP PILOTE +IA</h1>
            <p className="text-muted-foreground">Cosmétiques Artisans</p>
          </div>

          <Tabs defaultValue={defaultTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="login" data-testid="tab-login">Connexion</TabsTrigger>
              <TabsTrigger value="register" data-testid="tab-register">Inscription</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              {twoFA ? (
                <Card data-testid="card-2fa">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <ShieldCheck size={20} className="text-[#205F79]" />
                      Vérification administrateur
                    </CardTitle>
                    <CardDescription>
                      Un code à 6 chiffres a été envoyé à <strong>{twoFA.email_hint}</strong>. Il est valide 10 minutes.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleVerify2FA} className="space-y-4">
                      <div>
                        <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">
                          Code de vérification
                        </label>
                        <Input
                          data-testid="input-2fa-code"
                          type="text"
                          inputMode="numeric"
                          autoComplete="one-time-code"
                          maxLength={6}
                          placeholder="123456"
                          value={code}
                          onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                          className="text-center text-2xl font-mono tracking-[0.5em]"
                          autoFocus
                          required
                        />
                      </div>
                      <Button data-testid="btn-verify-2fa" type="submit" className="w-full" disabled={loading || code.length < 6}>
                        {loading ? (
                          <>
                            <Loader2 className="mr-2 animate-spin" size={18} />
                            Vérification...
                          </>
                        ) : (
                          'Valider le code'
                        )}
                      </Button>
                      <div className="flex justify-between text-sm">
                        <button
                          type="button"
                          onClick={handleResend2FA}
                          disabled={loading}
                          data-testid="btn-resend-2fa"
                          className="text-[#205F79] hover:underline disabled:opacity-50"
                        >
                          Renvoyer le code
                        </button>
                        <button
                          type="button"
                          onClick={handleCancel2FA}
                          data-testid="btn-cancel-2fa"
                          className="text-muted-foreground hover:text-foreground"
                        >
                          Annuler
                        </button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              ) : (
              <Card>
                <CardHeader>
                  <CardTitle>Connexion</CardTitle>
                  <CardDescription>Connectez-vous à votre compte</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">
                        Email
                      </label>
                      <Input
                        data-testid="login-email"
                        type="email"
                        placeholder="votre@email.com"
                        value={loginData.email}
                        onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">
                        Mot de passe
                      </label>
                      <Input
                        data-testid="login-password"
                        type="password"
                        placeholder="••••••••"
                        value={loginData.password}
                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                        required
                      />
                    </div>
                    <Button data-testid="btn-login" type="submit" className="w-full" disabled={loading}>
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 animate-spin" size={18} />
                          Connexion...
                        </>
                      ) : (
                        'Se connecter'
                      )}
                    </Button>
                    
                    <div className="text-center mt-4">
                      <button
                        type="button"
                        onClick={() => navigate('/forgot-password')}
                        className="text-sm text-[#205F79] hover:text-[#1a4d61] hover:underline"
                      >
                        Mot de passe oublié ?
                      </button>
                    </div>
                  </form>
                </CardContent>
              </Card>
              )}
            </TabsContent>

            <TabsContent value="register">
              <Card>
                <CardHeader>
                  <CardTitle>Inscription</CardTitle>
                  <CardDescription>Créez votre compte laboratoire</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">
                        Nom complet
                      </label>
                      <Input
                        data-testid="register-name"
                        type="text"
                        placeholder="Jean Dupont"
                        value={registerData.name}
                        onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">
                        Email
                      </label>
                      <Input
                        data-testid="register-email"
                        type="email"
                        placeholder="votre@email.com"
                        value={registerData.email}
                        onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">
                        Entreprise (optionnel)
                      </label>
                      <Input
                        data-testid="register-company"
                        type="text"
                        placeholder="Laboratoire Artisanal"
                        value={registerData.company}
                        onChange={(e) => setRegisterData({ ...registerData, company: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">
                        Mot de passe
                      </label>
                      <Input
                        data-testid="register-password"
                        type="password"
                        placeholder="••••••••"
                        value={registerData.password}
                        onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                        required
                      />
                    </div>
                    {/* CGU checkbox */}
                    <label className="flex items-start gap-3 p-3 rounded-md border border-gray-200 hover:border-[#205F79]/40 transition-colors cursor-pointer" data-testid="register-cgu-label">
                      <input
                        type="checkbox"
                        checked={acceptedCGU}
                        onChange={(e) => setAcceptedCGU(e.target.checked)}
                        className="mt-1 w-4 h-4 accent-[#205F79] cursor-pointer"
                        data-testid="register-cgu-checkbox"
                      />
                      <span className="text-xs text-gray-700 leading-relaxed">
                        J'accepte les{' '}
                        <a href="/legal/cgu" target="_blank" rel="noopener noreferrer" className="text-[#205F79] underline hover:no-underline font-medium">
                          Conditions Générales d'Utilisation et de Vente
                        </a>{' '}
                        et la{' '}
                        <a href="/legal/confidentialite" target="_blank" rel="noopener noreferrer" className="text-[#205F79] underline hover:no-underline font-medium">
                          Politique de confidentialité (RGPD)
                        </a>.
                        Je reconnais notamment que <strong>l'assistant IA est un outil d'assistance</strong> et que je reste seul responsable
                        de la conformité réglementaire de mes produits cosmétiques.
                      </span>
                    </label>
                    <Button data-testid="btn-register" type="submit" className="w-full" disabled={loading || !acceptedCGU}>
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 animate-spin" size={18} />
                          Création...
                        </>
                      ) : (
                        'Créer mon compte'
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default Auth;
